import { useCallback, useRef, useState } from 'react';

const STORAGE_PREFIX = 'zeettu.listFilters.';
const LIST_TAB_PREFIX = 'zeettu.listTab.';

type Primitive = string | number | boolean;

function isPrimitive(value: unknown): value is Primitive {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

/**
 * Keep only keys that exist in `defaults` and whose stored value is shape-compatible
 * with the default value. Keys listed in `omit` (e.g. `page`) always fall back to the default.
 */
function sanitize<T extends object>(
  raw: unknown,
  defaults: T,
  omit?: readonly (keyof T)[],
): T {
  const result: Record<string, unknown> = { ...(defaults as Record<string, unknown>) };
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return result as T;
  const source = raw as Record<string, unknown>;
  const omitSet = new Set<string>((omit ?? []).map(String));

  for (const key of Object.keys(defaults as Record<string, unknown>)) {
    if (omitSet.has(key)) continue;
    if (!Object.prototype.hasOwnProperty.call(source, key)) continue;
    const stored = source[key];
    const fallback = (defaults as Record<string, unknown>)[key];

    if (Array.isArray(fallback)) {
      if (Array.isArray(stored)) result[key] = stored;
      continue;
    }
    if (fallback === undefined || fallback === null) {
      // Optional filter (e.g. `employeeId: undefined as string | undefined`).
      if (stored === null || isPrimitive(stored)) result[key] = stored;
      continue;
    }
    if (isPrimitive(fallback)) {
      if (typeof stored === typeof fallback) result[key] = stored;
      continue;
    }
    // Nested objects are not supported for persistence; keep the default.
  }
  return result as T;
}

export function readPersistedListFilters<T extends object>(
  key: string,
  defaults: T,
  omit?: readonly (keyof T)[],
): T {
  try {
    const raw = sessionStorage.getItem(`${STORAGE_PREFIX}${key}`);
    if (!raw) return { ...defaults };
    return sanitize(JSON.parse(raw), defaults, omit);
  } catch {
    return { ...defaults };
  }
}

export function writePersistedListFilters<T extends object>(
  key: string,
  value: T,
  omit?: readonly (keyof T)[],
): void {
  try {
    const toStore: Record<string, unknown> = { ...(value as Record<string, unknown>) };
    for (const k of omit ?? []) delete toStore[String(k)];
    sessionStorage.setItem(`${STORAGE_PREFIX}${key}`, JSON.stringify(toStore));
  } catch {
    /* ignore quota / private mode */
  }
}

/** Remove every persisted list filter and list tab entry (called on logout). */
export function clearPersistedListState(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (k && (k.startsWith(STORAGE_PREFIX) || k.startsWith(LIST_TAB_PREFIX))) keys.push(k);
    }
    keys.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

export type PersistedListFiltersOptions<T extends object> = {
  /** Keys that must never be persisted/restored (e.g. `page`). */
  omit?: readonly (keyof T)[];
};

/**
 * Persist a list page's applied filter state for the browser session so returning to the
 * page restores the same filters (sibling of `usePersistedListTab`).
 *
 * Returns `[filters, setFilters, reset]`. `setFilters` accepts a value or an updater and
 * writes through to sessionStorage synchronously; `reset` restores `defaults`.
 */
export function usePersistedListFilters<T extends object>(
  key: string,
  defaults: T,
  options?: PersistedListFiltersOptions<T>,
): [T, (next: T | ((prev: T) => T)) => void, () => void] {
  const defaultsRef = useRef(defaults);
  const omitRef = useRef(options?.omit);
  omitRef.current = options?.omit;

  const [filters, setFiltersState] = useState<T>(() =>
    readPersistedListFilters(key, defaultsRef.current, omitRef.current),
  );
  const latestRef = useRef(filters);
  latestRef.current = filters;

  const setFilters = useCallback(
    (next: T | ((prev: T) => T)) => {
      const resolved =
        typeof next === 'function' ? (next as (prev: T) => T)(latestRef.current) : next;
      latestRef.current = resolved;
      setFiltersState(resolved);
      writePersistedListFilters(key, resolved, omitRef.current);
    },
    [key],
  );

  const reset = useCallback(() => {
    const next = { ...defaultsRef.current };
    latestRef.current = next;
    setFiltersState(next);
    writePersistedListFilters(key, next, omitRef.current);
  }, [key]);

  return [filters, setFilters, reset];
}

/**
 * Convenience wrapper for a single primitive or string[] (search text, showFilters, page size).
 * `T` is intentionally unconstrained so literal defaults widen (`false` -> `boolean`);
 * pass an explicit generic for literal unions, e.g. `usePersistedListValue<'' | 'true'>(key, '')`.
 */
export function usePersistedListValue<T>(
  key: string,
  defaultValue: T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const defaultsRef = useRef({ value: defaultValue });
  const [state, setState] = usePersistedListFilters(key, defaultsRef.current);

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      setState((prev) => ({
        value: typeof next === 'function' ? (next as (p: T) => T)(prev.value) : next,
      }));
    },
    [setState],
  );

  return [state.value, setValue];
}
